import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import classes from './styles.module.scss';
import FullscreenClose from './fullscreen-close.component';
import { useKato } from '../../../../contexts/kato.context';
import { IProductSelfSufficiencyStats } from '../../../../types/kato.types';
import { getLang } from '../../../../helpers/utils';
import { SortIcon } from '../../../../assets/icons';

const baseClass = 'food-fullscreen';
const additionalClass = 'budget-investment-projects-fullscreen';
const translationKey = 'cards.food-card';

const headers = [
    "productName",
    "annualConsumptionPerPerson",
    "annualConsumption",
    "annualProductProduction",
    "selfSufficiencyPercentage",
] as const;

type HeaderKey = typeof headers[number];

export const getPercentage = (item: IProductSelfSufficiencyStats): number => {
    const production = item.annualProductProduction || 0;
    const consumption = item.annualConsumption || 0;
    return production && consumption ? +(production / consumption * 100).toFixed(1) : 0;
};

const FoodFullscreen = () => {
    const { t } = useTranslation();
    const { productSelfSufficiencyStats } = useKato().katoGeneralInfo;

    const [sortedData, setSortedData] = useState<IProductSelfSufficiencyStats[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: HeaderKey; direction: 'asc' | 'desc' } | null>(null);

    // Подготовка данных
    const data = (productSelfSufficiencyStats || [])
        .map((item) => ({
            ...item,
            selfSufficiencyPercentage: getPercentage(item),
        }))
        .sort((a, b) => b.selfSufficiencyPercentage - a.selfSufficiencyPercentage);

    useEffect(() => {
        setSortedData(data);
    }, [productSelfSufficiencyStats]);

    if (!data.length) return null;

    const handleSort = (key: HeaderKey) => {
        const direction = sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';

        const sorted = [...sortedData].sort((a, b) => {
            if (key === 'productName') {
                const nameA = a[`productName${getLang()}`] || '';
                const nameB = b[`productName${getLang()}`] || '';
                return direction === 'asc'
                    ? nameA.localeCompare(nameB)
                    : nameB.localeCompare(nameA);
            }

            const valA = +(a[key] || 0);
            const valB = +(b[key] || 0);
            return direction === 'asc' ? valA - valB : valB - valA;
        });

        setSortedData(sorted);
        setSortConfig({ key, direction });
    };

    return (
        <div className={`${classes[`${additionalClass}`]}`}>
            <FullscreenClose />
            <div className={`${classes[`${additionalClass}__table`]}`}>
                <table>
                    <thead>
                        <tr>
                            <th>№</th>
                            {headers.map((key) => (
                                <th key={key}>
                                    <div className={classes[`${additionalClass}__header`]}>
                                        {t(`${translationKey}.table.${key}`)}
                                        <SortIcon onClick={() => handleSort(key)} />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData.map((item, idx) => (
                            <tr key={idx}>
                                <td>{idx + 1}</td>
                                {headers.map((key) => {
                                    let value: string | number = '-';
                                    if (key === 'productName') {
                                        value = item[`productName${getLang()}`] || '-';
                                    } else if (key === 'selfSufficiencyPercentage') {
                                        value = `${item.selfSufficiencyPercentage}%`;
                                    } else {
                                        const raw = item[key as keyof IProductSelfSufficiencyStats];
                                        value = typeof raw === 'number' ? raw.toLocaleString() : '-';
                                    }
                                    return <td key={key}>{value}</td>;
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FoodFullscreen;
